import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import {
  setProblems,
  addProblem,
  updateProblem,
  removeProblem,
  setLoading,
  setError,
  updateProblemStatus as updateProblemStatusAction, // <-- This is important
} from '../redux/slices/problemsSlice';
import { Problem, ProblemStatus } from '../models/Problem';
import {
  addProblemToFirestore,
  deleteProblemFromFirestore,
  updateProblemStatusInFirestore,
  getProblemsFromFirestore,
} from '../services/problemService';

export const useProblem = () => {
  const dispatch = useDispatch();
  const { problems, loading, error } = useSelector((state: RootState) => state.problems);
  const { user } = useSelector((state: RootState) => state.user);

  const loadProblems = useCallback(async () => {
    if (!user || !user.id) {
      dispatch(setError('User ID not found'));
      return;
    }

    try {
      dispatch(setLoading(true));
      const fetchedProblems = await getProblemsFromFirestore(user.id);
      dispatch(setProblems(fetchedProblems));
      dispatch(setLoading(false));
    } catch (error) {
      console.error('Error loading problems from Firestore:', error);
      dispatch(setError('Error loading problems from Firestore'));
      dispatch(setLoading(false));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (user && user.id) {
      loadProblems();
    }
  }, [user, loadProblems]);

  const createProblem = useCallback(
    async (problem: Omit<Problem, 'id'>) => {
      if (!user || !user.id) {
        dispatch(setError('User ID not found'));
        return;
      }

      try {
        dispatch(setLoading(true));
        const problemId = await addProblemToFirestore(user.id, problem);
        const newProblem: Problem = {
          ...problem,
          id: problemId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        dispatch(addProblem(newProblem));
        dispatch(setLoading(false));
      } catch (error) {
        console.error('Error saving problem to Firestore:', error);
        dispatch(setError('Error saving problem to Firestore'));
        dispatch(setLoading(false));
      }
    },
    [dispatch, user]
  );

  // In useProblem.ts
  const updateProblemStatus = useCallback(
    async (id: string, status: ProblemStatus) => {
      if (!user || !user.id) {
        dispatch(setError('User ID not found'));
        return;
      }

      try {
        dispatch(setLoading(true));
        await updateProblemStatusInFirestore(id, status);
        // Fix: Call the action creator correctly
        dispatch(updateProblemStatusAction({ id, status }));
        dispatch(setLoading(false));
      } catch (error) {
        console.error('Error updating problem status in Firestore:', error);
        dispatch(setError('Error updating problem status in Firestore'));
        dispatch(setLoading(false));
      }
    },
    [dispatch, user]
  );
  const deleteProblem = useCallback(
    async (id: string) => {
      if (!user || !user.id) {
        dispatch(setError('User ID not found'));
        return;
      }

      try {
        dispatch(setLoading(true));
        await deleteProblemFromFirestore(user.id, id);
        dispatch(removeProblem(id));
        dispatch(setLoading(false));
      } catch (error) {
        console.error('Error deleting problem from Firestore:', error);
        dispatch(setError('Error deleting problem from Firestore'));
        dispatch(setLoading(false));
      }
    },
    [dispatch, user]
  );

  return {
    problems,
    loading,
    error,
    loadProblems,
    createProblem,
    updateProblemStatus,
    deleteProblem,
    setLoading: (isLoading: boolean) => dispatch(setLoading(isLoading)),
    setError: (errorMessage: string | null) => dispatch(setError(errorMessage)),
  };
};
